import {
  useCallback,
  useEffect,
  useState,
} from "react";

import sbiLogo from "../../assets/images/sbi.png";
import bomLogo from "../../assets/images/bom.png";
import bobLogo from "../../assets/images/bob.png";
import uidLogo from "../../assets/images/uid.png";
import panLogo from "../../assets/images/pan.png";

const FORMS_ENDPOINT =
  "/backend/api/form_data.php";

/*
 * Select the logo using module, name,
 * title or route.
 */
const logoForForm = (form) => {
  const identity = [
    form?.module,
    form?.name,
    form?.title,
    form?.route,
  ]
    .filter(Boolean)
    .join(" ")
    .toUpperCase();

  if (identity.includes("BOM")) {
    return bomLogo;
  }

  if (identity.includes("BOB")) {
    return bobLogo;
  }

  if (identity.includes("UID")) {
    return uidLogo;
  }

  if (identity.includes("PAN")) {
    return panLogo;
  }

  return sbiLogo;
};

/*
 * Determine whether the form is visible.
 */
const isVisible = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return ![
    "0",
    "no",
    "false",
    "hide",
    "hidden",
    "n",
  ].includes(normalized);
};

/*
 * Convert database values into the required
 * frontend form-card structure.
 */
const normalizeForm = (form) => ({
  id: Number(form?.id || 0),

  name: String(
    form?.name || ""
  ).trim(),

  title: String(
    form?.title || ""
  ).trim(),

  priority: Number(
    form?.priority || 0
  ),

  visible: String(
    form?.visible || ""
  ).trim(),

  charge: Number(
    form?.charge || 0
  ),

  module: String(
    form?.module || ""
  )
    .trim()
    .toUpperCase(),

  route: String(
    form?.route || ""
  ).trim(),

  modifyroute: String(
    form?.modifyroute || ""
  ).trim(),

  sampleroute: String(
    form?.sampleroute || ""
  ).trim(),

  formCount: Number(
    form?.form_count || 0
  ),

  image: logoForForm(form),
});

const normalizeVisibleForms = (items) =>
  (Array.isArray(items) ? items : [])
    .map(normalizeForm)
    .filter(
      (form) =>
        form.id > 0 &&
        isVisible(form.visible)
    );

/*
 * Request both the complete form list and the
 * six most-used forms from form_data.php.
 */
const requestFormData = async (signal) => {
  const response = await fetch(
    FORMS_ENDPOINT,
    {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
      signal,
    }
  );

  const responseText =
    await response.text();

  let result;

  try {
    result = responseText
      ? JSON.parse(responseText)
      : {};
  } catch {
    throw new Error(
      responseText.trim()
        ? `Invalid server response: ${responseText.substring(
            0,
            200
          )}`
        : `form_data.php returned an empty response. HTTP ${response.status}.`
    );
  }

  if (
    !response.ok ||
    !result.success
  ) {
    throw new Error(
      result.message ||
        `Unable to load forms. HTTP ${response.status}.`
    );
  }

  const forms = normalizeVisibleForms(
    result.forms
  ).sort(
    (first, second) =>
      first.priority -
        second.priority ||
      first.id - second.id
  );

  const mostUsed = normalizeVisibleForms(
    result.mostUsed
  )
    .sort(
      (first, second) =>
        second.formCount -
          first.formCount ||
        first.priority -
          second.priority ||
        first.id - second.id
    )
    .slice(0, 6);

  return {
    forms,
    mostUsed,
  };
};

/*
 * Backward-compatible loader for code that only
 * needs the complete form list.
 */
export const loadForms = async (signal) => {
  const data =
    await requestFormData(signal);

  return data.forms;
};

/*
 * React hook used by FillMyForm.jsx and
 * FormDetails.jsx.
 */
export const useFormData = () => {
  const [forms, setForms] =
    useState([]);

  const [mostUsed, setMostUsed] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const refreshForms = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");

      try {
        const loadedData =
          await requestFormData(signal);

        setForms(loadedData.forms);

        setMostUsed(
          loadedData.mostUsed
        );
      } catch (requestError) {
        if (
          requestError?.name !==
          "AbortError"
        ) {
          setForms([]);
          setMostUsed([]);

          setError(
            requestError instanceof Error
              ? requestError.message
              : "Unable to load forms."
          );
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const controller =
      new AbortController();

    refreshForms(
      controller.signal
    );

    return () => {
      controller.abort();
    };
  }, [refreshForms]);

  const reload = useCallback(() => {
    refreshForms();
  }, [refreshForms]);

  return {
    forms,
    mostUsed,
    loading,
    error,
    reload,
  };
};

/*
 * Create Form Details page URL.
 */
export const getFormDetailsUrl = (
  form
) => {
  if (!form?.route) {
    return "/Fill_My_Form";
  }

  return `/form-details?route=${encodeURIComponent(
    form.route
  )}`;
};

/*
 * Return the PDF sample route stored
 * in the sampleroute column.
 */
export const getSamplePdfPath = (
  form
) => {
  if (form?.sampleroute) {
    return form.sampleroute;
  }

  const routeParts = String(
    form?.route || ""
  )
    .split("/")
    .filter(Boolean)
    .slice(-2);

  if (
    routeParts.length === 0
  ) {
    return "";
  }

  return `/SampleForms/${routeParts.join(
    "_"
  )}.pdf`;
};